/**
 * useOfflineQueue — AC7.
 *
 * Queues mid-attempt mutations to localStorage when they fail due to network errors.
 * Automatically flushes the queue when the browser reconnects (online event).
 *
 * localStorage key: 'bimassess-attempt-queue'
 * Each queue entry: { url, method, body, headers? }
 */
import { useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/api-client';

const STORAGE_KEY = 'bimassess-attempt-queue';

interface QueuedMutation {
  url: string;
  method: string;
  body: string;
  headers?: Record<string, string>;
}

interface OfflineQueueAPI {
  enqueue: (mutation: Omit<QueuedMutation, never>) => void;
}

function readQueue(): QueuedMutation[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as QueuedMutation[];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedMutation[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

export function useOfflineQueue(): OfflineQueueAPI {
  const flushingRef = useRef(false);

  const flush = useCallback(async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;

    const queue = readQueue();
    if (queue.length === 0) {
      flushingRef.current = false;
      return;
    }

    // Flush all items sequentially
    const remaining: QueuedMutation[] = [];
    for (const item of queue) {
      try {
        const res = await apiFetch(item.url, {
          method: item.method,
          body: item.body,
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
            ...item.headers,
          },
        });
        if (!res.ok) {
          remaining.push(item);
        }
      } catch {
        // Still offline or network error — keep in queue
        remaining.push(item);
      }
    }

    writeQueue(remaining);
    flushingRef.current = false;
  }, []);

  const enqueue = useCallback(
    (mutation: Omit<QueuedMutation, never>) => {
      const queue = readQueue();
      queue.push(mutation);
      writeQueue(queue);
    },
    [],
  );

  useEffect(() => {
    function onOnline() {
      void flush();
    }

    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
    };
  }, [flush]);

  return { enqueue };
}
